import { useEffect, useRef, useState, type DragEvent } from "react";
import { TaskCard } from "./TaskCard";
import { IcoPlus } from "../../components/icons";
import type { Task, TaskType, UserRole } from "../../types/domain";
import type { WeekDay } from "../../lib/weekDates";

interface Props {
  /** Полный объект дня недели или null для колонки «Без даты». */
  day: WeekDay | null;
  tasks: Task[];
  taskTypes: TaskType[];
  draggingTaskId: string | null;
  /** true если у пользователя есть глобальный write-доступ к разделу задач. */
  canWrite: boolean;
  /** id текущего пользователя, нужен staff-у для проверки «своя задача vs чужая». */
  meId: string | null;
  meRole: UserRole | null;
  /** id запуска, выбранного в Topbar/Sidebar. Карточки этого запуска
   *  получают левую цветную полосу по launch.color (мягкая подсветка). */
  activeLaunchId?: string | null;
  /** Цвет активного запуска, передаётся карточкам с launchId === activeLaunchId. */
  activeLaunchColor?: string | null;
  onOpen: (task: Task) => void;
  onAdd: () => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (dueDate: string | null, targetIndex: number, event: DragEvent<HTMLDivElement>) => void;
}

/** Может ли текущий пользователь editить эту конкретную задачу.
 *  Зеркало серверного `assertCanEditTask`. */
function canEditTaskFor(
  task: Task,
  meId: string | null,
  meRole: UserRole | null,
): boolean {
  if (!meId || !meRole) return false;
  if (meRole === "editor" || meRole === "admin") return true;
  if (meRole === "staff") {
    return task.assigneeId === meId || task.createdById === meId;
  }
  return false;
}

export function TaskColumn({
  day,
  tasks,
  taskTypes,
  draggingTaskId,
  canWrite,
  meId,
  meRole,
  activeLaunchId = null,
  activeLaunchColor = null,
  onOpen,
  onAdd,
  onDragStart,
  onDragEnd,
  onDrop,
}: Props) {
  const typeById = new Map(taskTypes.map((t) => [t.id, t]));
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const dragDepth = useRef(0);
  const [over, setOver] = useState(false);

  useEffect(() => {
    const reset = () => {
      dragDepth.current = 0;
      setOver(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, []);

  const computeTargetIndex = (e: DragEvent<HTMLDivElement>): number => {
    const body = bodyRef.current;
    if (!body) return tasks.length;
    const nodes = Array.from(
      body.querySelectorAll<HTMLElement>(".card[data-task-id]"),
    );
    const y = e.clientY;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]!;
      if (draggingTaskId && node.dataset.taskId === draggingTaskId) continue;
      const rect = node.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) return i;
    }
    return tasks.length;
  };

  const handleDragEnter = () => {
    dragDepth.current += 1;
    if (!over) setOver(true);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setOver(false);
    const idx = computeTargetIndex(e);
    onDrop(day ? day.iso : null, idx, e);
  };

  const isToday = day?.isToday ?? false;
  const isWeekend = day?.isWeekend ?? false;

  return (
    <div
      className={
        "column" + (day ? "" : " column-undated") + (isWeekend ? " column-weekend" : "")
      }
    >
      <div className={"column-head" + (isToday ? " is-today" : "")}>
        <div className="col-date-row">
          <span className="col-weekday">{day ? day.weekdayFull : "Без даты"}</span>
          <div className="col-date-right">
            {isToday ? (
              <span className="today-badge">
                <span className="live-dot" />
                Сегодня
              </span>
            ) : day ? (
              <span className="col-date">
                {day.dayOfMonth} {day.monthGenitive}
              </span>
            ) : (
              <span className="col-date col-date-undated">{tasks.length}</span>
            )}
          </div>
        </div>
        <div className="col-meta-row">
          <span className="col-count">
            <b>{tasks.length}</b> {pluralTasks(tasks.length)}
          </span>
          {canWrite && (
            <button type="button" className="col-add" onClick={onAdd}>
              <IcoPlus size={12} /> Добавить
            </button>
          )}
        </div>
      </div>
      <div
        ref={bodyRef}
        className={"col-body" + (over ? " drag-over" : "")}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {tasks.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            type={typeById.get(t.taskTypeId) ?? null}
            onOpen={() => onOpen(t)}
            onDragStart={() => onDragStart(t.id)}
            onDragEnd={onDragEnd}
            isDragging={draggingTaskId === t.id}
            draggable={canWrite && canEditTaskFor(t, meId, meRole)}
            launchAccent={
              activeLaunchId && t.launchId === activeLaunchId ? activeLaunchColor : null
            }
          />
        ))}
        {tasks.length === 0 && canWrite && (
          <button type="button" className="card-add-empty" onClick={onAdd}>
            <IcoPlus size={14} /> Добавить задачу
          </button>
        )}
      </div>
    </div>
  );
}

function pluralTasks(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "задача";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "задачи";
  return "задач";
}
